import { Typography } from "antd";
import { getSemanticEnumTagColor, type SemanticEnumRegistry } from "./semantic-enum";
import {
  SHARED_ASSET_PICKER_SCOPES,
  type SharedAssetPickerScope,
} from "./shared-asset-picker-modal";
import { readTableFilterValue } from "./table-filter-values";

export type EnvironmentImagePickerControlledFilters = Record<
  string,
  (string | number | boolean)[] | null | undefined
>;

const ENVIRONMENT_IMAGE_PICKER_CATEGORY_VALUES = ["endpoint", "network", "security"] as const;
const ENVIRONMENT_IMAGE_PICKER_ARCHITECTURE_VALUES = ["x86_64", "arm64", "i386", "other"] as const;
const ENVIRONMENT_IMAGE_PICKER_SHARE_MODE_VALUES = [
  "private",
  "shared_to_users",
  "public",
] as const;
const ENVIRONMENT_IMAGE_PICKER_STATUS_VALUES = ["active", "draft"] as const;

type EnvironmentImagePickerCategory = (typeof ENVIRONMENT_IMAGE_PICKER_CATEGORY_VALUES)[number];
type EnvironmentImagePickerArchitecture =
  (typeof ENVIRONMENT_IMAGE_PICKER_ARCHITECTURE_VALUES)[number];
type EnvironmentImagePickerShareMode = (typeof ENVIRONMENT_IMAGE_PICKER_SHARE_MODE_VALUES)[number];
type EnvironmentImagePickerStatus = (typeof ENVIRONMENT_IMAGE_PICKER_STATUS_VALUES)[number];

const ENVIRONMENT_IMAGE_PICKER_CATEGORY_PRESENTATION_REGISTRY = {
  endpoint: { tone: "processing", tagColor: "blue" },
  network: { tone: "processing", tagColor: "cyan" },
  security: { tone: "error", tagColor: "red" },
} as const satisfies SemanticEnumRegistry<EnvironmentImagePickerCategory>;

const ENVIRONMENT_IMAGE_PICKER_ARCHITECTURE_PRESENTATION_REGISTRY = {
  x86_64: { tone: "processing", tagColor: "blue" },
  arm64: { tone: "processing", tagColor: "cyan" },
  i386: { tone: "default" },
  other: { tone: "default" },
} as const satisfies SemanticEnumRegistry<EnvironmentImagePickerArchitecture>;

const ENVIRONMENT_IMAGE_PICKER_STATUS_PRESENTATION_REGISTRY = {
  active: { tone: "success" },
  draft: { tone: "warning" },
} as const satisfies SemanticEnumRegistry<EnvironmentImagePickerStatus>;

const ENVIRONMENT_IMAGE_PICKER_SHARE_MODE_PRESENTATION_REGISTRY = {
  private: { tone: "default" },
  shared_to_users: { tone: "processing", tagColor: "cyan" },
  public: { tone: "success", tagColor: "green" },
} as const satisfies SemanticEnumRegistry<EnvironmentImagePickerShareMode>;

function readPickerSemanticEnumValue<TValue extends string>(
  values: readonly TValue[],
  value?: string | null,
): TValue | null {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return null;
  return values.includes(text as TValue) ? (text as TValue) : null;
}

function getPickerSemanticEnumTagColor<TValue extends string>(
  values: readonly TValue[],
  registry: SemanticEnumRegistry<TValue>,
  value?: string | null,
) {
  return getSemanticEnumTagColor(registry, readPickerSemanticEnumValue(values, value), "warning");
}

export function getEnvironmentImagePickerCategoryTagColor(value?: string | null) {
  return getPickerSemanticEnumTagColor(
    ENVIRONMENT_IMAGE_PICKER_CATEGORY_VALUES,
    ENVIRONMENT_IMAGE_PICKER_CATEGORY_PRESENTATION_REGISTRY,
    value,
  );
}

export function getEnvironmentImagePickerArchitectureTagColor(value?: string | null) {
  return getPickerSemanticEnumTagColor(
    ENVIRONMENT_IMAGE_PICKER_ARCHITECTURE_VALUES,
    ENVIRONMENT_IMAGE_PICKER_ARCHITECTURE_PRESENTATION_REGISTRY,
    value,
  );
}

export function getEnvironmentImagePickerStatusTagColor(value?: string | null) {
  return getPickerSemanticEnumTagColor(
    ENVIRONMENT_IMAGE_PICKER_STATUS_VALUES,
    ENVIRONMENT_IMAGE_PICKER_STATUS_PRESENTATION_REGISTRY,
    value,
  );
}

export function getEnvironmentImagePickerShareModeTagColor(value?: string | null) {
  return getPickerSemanticEnumTagColor(
    ENVIRONMENT_IMAGE_PICKER_SHARE_MODE_VALUES,
    ENVIRONMENT_IMAGE_PICKER_SHARE_MODE_PRESENTATION_REGISTRY,
    value,
  );
}

export function renderEnvironmentImagePickerCellText(
  value?: string | null,
  { code = false }: { code?: boolean } = {},
) {
  const text = String(value || "").trim();
  return (
    <Typography.Text
      code={code}
      ellipsis={text ? { tooltip: text } : false}
      style={{ display: "block", maxWidth: "100%" }}
    >
      {text}
    </Typography.Text>
  );
}

export function readEnvironmentImagePickerFilterValues(
  filters: EnvironmentImagePickerControlledFilters,
) {
  const values: Record<string, string> = {};
  const entries = [
    ["code", "ref_code"],
    ["name", "ref_name"],
    ["description", "description"],
    ["category", "category"],
    ["usageType", "usage_type"],
    ["status", "status"],
    ["shareMode", "share_mode"],
  ] as const;
  for (const [name, key] of entries) {
    const value = readTableFilterValue(filters, key);
    if (value !== undefined) values[name] = value;
  }
  return values;
}

export function normalizeEnvironmentImagePickerAllowedScopes(
  allowedScopes?: readonly SharedAssetPickerScope[],
) {
  if (!allowedScopes) return SHARED_ASSET_PICKER_SCOPES;
  if (allowedScopes.length === 0) {
    throw new Error("ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: allowedScopes must not be empty");
  }
  for (const scope of allowedScopes) {
    if (!SHARED_ASSET_PICKER_SCOPES.includes(scope)) {
      throw new Error(`ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: unsupported scope ${scope}`);
    }
  }
  return allowedScopes;
}
